import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

function getFallbackRatio(span) {
  if (span === "tall") return 0.8;
  if (span === "wide") return 1.72;
  return 1.33;
}

function getItemRatio(item) {
  return item.thumbnailRatio ?? getFallbackRatio(item.span);
}

function scoreColumns(colHeights) {
  return Math.max(...colHeights) - Math.min(...colHeights);
}

function computeLayout(items, colWidth, numCols, gap) {
  const colHeights = new Array(numCols).fill(0);
  const positions = {};

  items.forEach((item) => {
    const hasMedia = !!item.thumbnail;
    const ratio = getItemRatio(item);
    const forceSpan2 = item.gallerySpan === 2 && numCols >= 2;
    const isWideEnough = (ratio > 1.4 && numCols >= 2) || forceSpan2;
    const candidates = [];

    if (hasMedia) {
      // Single column — skipped if gallerySpan: 2
      if (!forceSpan2) {
        for (let c = 0; c < numCols; c++) {
          const h = colWidth / ratio;
          const y = colHeights[c];
          const nextHeights = [...colHeights];
          nextHeights[c] = y + h + gap;
          candidates.push({
            x: c * (colWidth + gap), y, w: colWidth, h,
            nextHeights, score: scoreColumns(nextHeights),
          });
        }
      }
      // Two-column span
      if (isWideEnough) {
        for (let c = 0; c <= numCols - 2; c++) {
          const itemW = colWidth * 2 + gap;
          const h = itemW / ratio;
          const y = Math.max(colHeights[c], colHeights[c + 1]);
          const nextHeights = [...colHeights];
          nextHeights[c] = y + h + gap;
          nextHeights[c + 1] = y + h + gap;
          candidates.push({
            x: c * (colWidth + gap), y, w: itemW, h,
            nextHeights, score: scoreColumns(nextHeights),
          });
        }
      }
    } else {
      // No thumbnail — place in shortest column, stretch to match tallest
      const maxH = Math.max(...colHeights);
      const col = colHeights.indexOf(Math.min(...colHeights));
      const y = colHeights[col];
      const fillH = Math.max(maxH - y, colWidth * 0.75);
      const nextHeights = [...colHeights];
      nextHeights[col] = y + fillH + gap;
      candidates.push({
        x: col * (colWidth + gap), y, w: colWidth, h: fillH,
        nextHeights, score: scoreColumns(nextHeights),
      });
    }

    if (candidates.length === 0) return;
    candidates.sort((a, b) => a.score - b.score);
    const best = candidates[0];
    positions[item.slug] = { x: best.x, y: best.y, w: best.w, h: best.h };
    for (let i = 0; i < numCols; i++) colHeights[i] = best.nextHeights[i];
  });

  return { positions, totalHeight: Math.max(...colHeights) };
}

function GalleryCard({ item, style }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    setHovered(true);
    if (videoRef.current) videoRef.current.play();
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="gcard"
      style={style}
      onClick={() => navigate(`/portfolio/${item.slug}`)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/portfolio/${item.slug}`)}
      aria-label={`View ${item.title}`}
    >
      <div className="gcard__media-inner">
        {item.thumbnail ? (
          item.mediaType === "video" ? (
            <video
              ref={videoRef}
              src={item.thumbnail}
              muted
              loop
              playsInline
              className="gcard__img"
              style={{ objectFit: "contain", background: "var(--c-off)" }}
            />
          ) : (
            <img
              src={item.thumbnail}
              alt={item.title}
              className="gcard__img"
              style={{ objectFit: "contain", background: "var(--c-off)" }}
              loading="lazy"
            />
          )
        ) : (
          <div
            className="gcard__placeholder"
            style={{ "--accent": item.accentColor }}
          >
            <div className="gcard__placeholder-types">
              {item.projectTypes.slice(0, 2).map((t) => (
                <span key={t} className="gcard__placeholder-type">{t}</span>
              ))}
            </div>
            <span className="gcard__placeholder-title">{item.title}</span>
          </div>
        )}
              </div>

        <div className={`gcard__overlay${hovered ? " gcard__overlay--on" : ""}`}>
          <div className="gcard__overlay-content">
            <p className="gcard__overlay-title">{item.title}</p>
            <p className="gcard__overlay-desc">{item.description}</p>
          </div>
        </div>

    </div>
  );
}

export default function MasonryGallery({ allItems = [], filteredItems = [] }) {
  const wrapRef = useRef(null);
  const [cw, setCw] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    setCw(wrapRef.current.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => setCw(e.contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  if (cw === null || cw === 0) {
    return <div ref={wrapRef} style={{ width: "100%", minHeight: 200 }} />;
  }

  const gap = 20;
  const numCols = cw > 1024 ? 3 : cw > 580 ? 2 : 1;
  const colWidth = (cw - gap * (numCols - 1)) / numCols;

  const { positions: visPos, totalHeight } = computeLayout(filteredItems, colWidth, numCols, gap);
  const { positions: allPos } = computeLayout(allItems, colWidth, numCols, gap);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        height: totalHeight,
        overflow: "hidden",
        transition: "height 0.5s ease",
      }}
    >
      {allItems.map((item) => {
        const isVisible = item.slug in visPos;
        const pos = isVisible ? visPos[item.slug] : allPos[item.slug];
        if (!pos) return null;

        return (
          <GalleryCard
            key={item.slug}
            item={item}
            style={{
              position: "absolute",
              left: pos.x,
              top: pos.y,
              width: pos.w,
              height: pos.h,
              opacity: isVisible ? 1 : 0,
              pointerEvents: isVisible ? "auto" : "none",
              transition: isVisible
                ? "left 0.5s cubic-bezier(0.4,0,0.2,1), top 0.5s cubic-bezier(0.4,0,0.2,1), width 0.5s ease, height 0.5s ease, opacity 0.3s ease"
                : "opacity 0.2s ease",
            }}
          />
        );
      })}
    </div>
  );
}